import { isInOverlay } from "src/pages/projects/board-shortcuts";

const TASK_PEEK_ATTRIBUTE = "data-task-peek";

// Space activates these, so a keyboard user on one gets the control, not the peek.
const SPACE_CONTROL_SELECTOR =
  'button, summary, input, [role="button"], [role="checkbox"], [role="switch"], [role="radio"], [role="tab"], [role="menuitem"], [role="option"]';

/** Marks a task's row or card, so Space over it previews the task, and pointing at it switches an open preview. */
export function taskPeekTargetProps(taskId: string) {
  return { [TASK_PEEK_ATTRIBUTE]: taskId } as const satisfies Record<
    typeof TASK_PEEK_ATTRIBUTE,
    string
  >;
}

/**
 * The task whose link has keyboard focus, else the task under the pointer.
 * A row inside another row, as a subtask in its parent, wins over the outer one.
 */
export function peekTargetTaskId(root: Document): string | null {
  // A keyboard user moving through rows wins over wherever the mouse happens to rest.
  const focused = root.activeElement;
  const focusedTaskId = focusedPeekTaskId(focused);
  if (focused && focusedTaskId && isFocusVisible(focused)) {
    return focusedTaskId;
  }
  const hovered = root.querySelectorAll(`[${TASK_PEEK_ATTRIBUTE}]:hover`);
  const innermost = hovered[hovered.length - 1];
  if (innermost) return innermost.getAttribute(TASK_PEEK_ATTRIBUTE);
  return focusedPeekTaskId(root.activeElement);
}

/** The task whose link has focus; a focused control inside a row keeps Space for itself. */
export function focusedPeekTaskId(focused: Element | null): string | null {
  if (!(focused instanceof HTMLAnchorElement)) return null;
  return peekTaskIdOf(focused);
}

/** The task whose row or card holds an event's target; the innermost row wins. */
export function peekTaskIdOf(target: EventTarget | null): string | null {
  if (!(target instanceof Element)) return null;
  return (
    target
      .closest(`[${TASK_PEEK_ATTRIBUTE}]`)
      ?.getAttribute(TASK_PEEK_ATTRIBUTE) ?? null
  );
}

// Long enough that a pointer crossing rows on its way elsewhere doesn't flash each one.
export const PEEK_HOVER_DWELL_MS = 130;

/** The task an open preview moves to when the pointer or focus lands on it, or null to stay. */
export function followedPeekTaskId({
  openTaskId,
  targetTaskId,
}: {
  openTaskId: string | null;
  targetTaskId: string | null;
}): string | null {
  if (openTaskId === null || targetTaskId === null) return null;
  return targetTaskId === openTaskId ? null : targetTaskId;
}

/** The task an open preview switches to once the pointer dwells on it, or null to schedule no switch. */
export function hoverPeekTaskId({
  openTaskId,
  hoveredTaskId,
  pointerType,
  isOverPanel,
}: {
  openTaskId: string | null;
  hoveredTaskId: string | null;
  pointerType: string;
  isOverPanel: boolean;
}): string | null {
  // A touch or pen "hover" is the start of a tap, which keeps its own meaning.
  if (pointerType !== "mouse" || isOverPanel) return null;
  return followedPeekTaskId({ openTaskId, targetTaskId: hoveredTaskId });
}

// Inputs that take no typing, so Space on them is a control's key rather than text.
const NON_TEXT_INPUT_TYPES = new Set([
  "checkbox",
  "radio",
  "button",
  "submit",
  "reset",
  "image",
  "range",
  "color",
  "file",
]);

function isEditable(element: Element): boolean {
  return (
    (element instanceof HTMLInputElement &&
      !NON_TEXT_INPUT_TYPES.has(element.type)) ||
    element instanceof HTMLTextAreaElement ||
    element instanceof HTMLSelectElement ||
    (element instanceof HTMLElement && element.isContentEditable) ||
    element.closest('[role="textbox"], [role="combobox"]') !== null
  );
}

/** Whether the peek leaves a key alone: typing, a shortcut, a control's own key, or an open dialog or menu. */
export function isPeekKeyIgnored(event: KeyboardEvent): boolean {
  if (event.defaultPrevented || event.isComposing) return true;
  if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) {
    return true;
  }
  const target = event.target;
  if (target instanceof Element) {
    // Escape in a plain field closes the preview, as its hint says; a combobox keeps it to close its own list.
    if (event.key === "Escape") {
      if (target.closest('[role="combobox"]') !== null) return true;
    } else if (isEditable(target)) {
      return true;
    }
    // A control a pointer left focused, as a picker after its menu closes, doesn't keep the key.
    if (
      event.key === " " &&
      target.matches(SPACE_CONTROL_SELECTOR) &&
      isFocusVisible(target)
    ) {
      return true;
    }
  }
  return isInOverlay(event);
}

function isFocusVisible(element: Element): boolean {
  try {
    return element.matches(":focus-visible");
  } catch {
    // Engines without the selector can't tell pointer focus from keyboard focus, so the control keeps the key.
    return true;
  }
}

export type PeekKey = "space" | "escape";

export function peekKey(event: KeyboardEvent): PeekKey | null {
  if (event.key === " ") return "space";
  if (event.key === "Escape") return "escape";
  return null;
}

/** What a key does to the peek: the task it shows next, and whether the key was used. */
export function nextPeek(
  key: PeekKey,
  {
    openTaskId,
    targetTaskId,
  }: { openTaskId: string | null; targetTaskId: string | null },
): { taskId: string | null; isHandled: boolean } {
  if (key === "escape") {
    return { taskId: null, isHandled: openTaskId !== null };
  }
  if (targetTaskId !== null && targetTaskId !== openTaskId) {
    return { taskId: targetTaskId, isHandled: true };
  }
  return { taskId: null, isHandled: openTaskId !== null };
}
